/**
 * Dependency-free QR Code encoder (byte mode).
 *
 * Compact adaptation of the public-domain "QR Code generator" reference
 * implementation by Project Nayuki (https://www.nayuki.io/page/qr-code-generator-library),
 * which is released to the public domain. Trimmed to byte-mode encoding with
 * automatic version selection and mask optimization — enough to encode an
 * otpauth:// TOTP setup URI without pulling in an npm package.
 *
 * Exposes generateQrMatrix(text) => boolean[][] (true = dark module).
 */

/* eslint-disable no-bitwise */

const ECC_MEDIUM = { ordinal: 0, formatBits: 0 };
// Codewords per block / blocks tables for ECC level M (index by version 1..40).
// Sourced from the QR spec via the Nayuki reference tables.
const ECC_CODEWORDS_PER_BLOCK_M = [
  -1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26,
  26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28,
];
const NUM_ERROR_CORRECTION_BLOCKS_M = [
  -1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16,
  17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49,
];

const getNumRawDataModules = (ver) => {
  let result = (16 * ver + 128) * ver + 64;
  if (ver >= 2) {
    const numAlign = Math.floor(ver / 7) + 2;
    result -= (25 * numAlign - 10) * numAlign - 55;
    if (ver >= 7) result -= 36;
  }
  return result;
};

const getNumDataCodewords = (ver) => (
  Math.floor(getNumRawDataModules(ver) / 8)
    - ECC_CODEWORDS_PER_BLOCK_M[ver] * NUM_ERROR_CORRECTION_BLOCKS_M[ver]
);

/* --- Reed-Solomon --- */

const reedSolomonMultiply = (x, y) => {
  let z = 0;
  for (let i = 7; i >= 0; i -= 1) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
};

const reedSolomonComputeDivisor = (degree) => {
  const result = [];
  for (let i = 0; i < degree - 1; i += 1) result.push(0);
  result.push(1);
  let root = 1;
  for (let i = 0; i < degree; i += 1) {
    for (let j = 0; j < result.length; j += 1) {
      result[j] = reedSolomonMultiply(result[j], root);
      if (j + 1 < result.length) result[j] ^= result[j + 1];
    }
    root = reedSolomonMultiply(root, 0x02);
  }
  return result;
};

const reedSolomonComputeRemainder = (data, divisor) => {
  const result = divisor.map(() => 0);
  data.forEach((b) => {
    const factor = b ^ result.shift();
    result.push(0);
    divisor.forEach((coef, i) => {
      result[i] ^= reedSolomonMultiply(coef, factor);
    });
  });
  return result;
};

/* --- Bit buffer / byte-mode segment --- */

const makeByteSegmentBits = (text) => {
  const utf8 = unescape(encodeURIComponent(text));
  const bytes = [];
  for (let i = 0; i < utf8.length; i += 1) bytes.push(utf8.charCodeAt(i));
  return bytes;
};

const appendBits = (val, len, bb) => {
  for (let i = len - 1; i >= 0; i -= 1) bb.push((val >>> i) & 1);
};

/* --- QR matrix builder --- */

class QrCode {
  constructor(version, ecl, dataCodewords, mask) {
    this.version = version;
    this.size = version * 4 + 17;
    this.mask = mask;
    this.modules = [];
    this.isFunction = [];
    for (let i = 0; i < this.size; i += 1) {
      this.modules.push(new Array(this.size).fill(false));
      this.isFunction.push(new Array(this.size).fill(false));
    }
    this.drawFunctionPatterns();
    const allCodewords = this.addEccAndInterleave(dataCodewords);
    this.drawCodewords(allCodewords);
    this.applyMask(mask);
    this.drawFormatBits(ecl, mask);
  }

  setFunctionModule(x, y, isDark) {
    this.modules[y][x] = isDark;
    this.isFunction[y][x] = true;
  }

  drawFinderPattern(x, y) {
    for (let dy = -4; dy <= 4; dy += 1) {
      for (let dx = -4; dx <= 4; dx += 1) {
        const dist = Math.max(Math.abs(dx), Math.abs(dy));
        const xx = x + dx;
        const yy = y + dy;
        if (xx >= 0 && xx < this.size && yy >= 0 && yy < this.size) {
          this.setFunctionModule(xx, yy, dist !== 2 && dist !== 4);
        }
      }
    }
  }

  drawAlignmentPattern(x, y) {
    for (let dy = -2; dy <= 2; dy += 1) {
      for (let dx = -2; dx <= 2; dx += 1) {
        this.setFunctionModule(x + dx, y + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
      }
    }
  }

  getAlignmentPatternPositions() {
    if (this.version === 1) return [];
    const numAlign = Math.floor(this.version / 7) + 2;
    const step = Math.floor((this.version * 8 + numAlign * 3 + 5) / (numAlign * 4 - 4)) * 2;
    const result = [6];
    for (let pos = this.size - 7; result.length < numAlign; pos -= step) {
      result.splice(1, 0, pos);
    }
    return result;
  }

  drawFunctionPatterns() {
    for (let i = 0; i < this.size; i += 1) {
      this.setFunctionModule(6, i, i % 2 === 0);
      this.setFunctionModule(i, 6, i % 2 === 0);
    }
    this.drawFinderPattern(3, 3);
    this.drawFinderPattern(this.size - 4, 3);
    this.drawFinderPattern(3, this.size - 4);

    const alignPos = this.getAlignmentPatternPositions();
    const numAlign = alignPos.length;
    for (let i = 0; i < numAlign; i += 1) {
      for (let j = 0; j < numAlign; j += 1) {
        if (!((i === 0 && j === 0) || (i === 0 && j === numAlign - 1) || (i === numAlign - 1 && j === 0))) {
          this.drawAlignmentPattern(alignPos[i], alignPos[j]);
        }
      }
    }

    this.drawFormatBits(ECC_MEDIUM, 0);
    this.drawVersion();
  }

  drawFormatBits(ecl, mask) {
    const data = (ecl.formatBits << 3) | mask;
    let rem = data;
    for (let i = 0; i < 10; i += 1) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    const bits = ((data << 10) | rem) ^ 0x5412;

    for (let i = 0; i <= 5; i += 1) this.setFunctionModule(8, i, ((bits >>> i) & 1) !== 0);
    this.setFunctionModule(8, 7, ((bits >>> 6) & 1) !== 0);
    this.setFunctionModule(8, 8, ((bits >>> 7) & 1) !== 0);
    this.setFunctionModule(7, 8, ((bits >>> 8) & 1) !== 0);
    for (let i = 9; i < 15; i += 1) this.setFunctionModule(14 - i, 8, ((bits >>> i) & 1) !== 0);

    for (let i = 0; i < 8; i += 1) this.setFunctionModule(this.size - 1 - i, 8, ((bits >>> i) & 1) !== 0);
    for (let i = 8; i < 15; i += 1) this.setFunctionModule(8, this.size - 15 + i, ((bits >>> i) & 1) !== 0);
    this.setFunctionModule(8, this.size - 8, true);
  }

  drawVersion() {
    if (this.version < 7) return;
    let rem = this.version;
    for (let i = 0; i < 12; i += 1) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
    const bits = (this.version << 12) | rem;
    for (let i = 0; i < 18; i += 1) {
      const bit = ((bits >>> i) & 1) !== 0;
      const a = this.size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      this.setFunctionModule(a, b, bit);
      this.setFunctionModule(b, a, bit);
    }
  }

  addEccAndInterleave(data) {
    const ver = this.version;
    const numBlocks = NUM_ERROR_CORRECTION_BLOCKS_M[ver];
    const blockEccLen = ECC_CODEWORDS_PER_BLOCK_M[ver];
    const rawCodewords = Math.floor(getNumRawDataModules(ver) / 8);
    const numShortBlocks = numBlocks - (rawCodewords % numBlocks);
    const shortBlockLen = Math.floor(rawCodewords / numBlocks);

    const blocks = [];
    const rsDiv = reedSolomonComputeDivisor(blockEccLen);
    let k = 0;
    for (let i = 0; i < numBlocks; i += 1) {
      const datLen = shortBlockLen - blockEccLen + (i < numShortBlocks ? 0 : 1);
      const dat = data.slice(k, k + datLen);
      k += datLen;
      const ecc = reedSolomonComputeRemainder(dat, rsDiv);
      if (i < numShortBlocks) dat.push(0);
      blocks.push(dat.concat(ecc));
    }

    const result = [];
    for (let i = 0; i < blocks[0].length; i += 1) {
      blocks.forEach((block, j) => {
        if (i !== shortBlockLen - blockEccLen || j >= numShortBlocks) {
          result.push(block[i]);
        }
      });
    }
    return result;
  }

  drawCodewords(data) {
    let i = 0;
    for (let right = this.size - 1; right >= 1; right -= 2) {
      const r = right === 6 ? 5 : right;
      for (let vert = 0; vert < this.size; vert += 1) {
        for (let j = 0; j < 2; j += 1) {
          const x = r - j;
          const upward = ((r + 1) & 2) === 0;
          const y = upward ? this.size - 1 - vert : vert;
          if (!this.isFunction[y][x] && i < data.length * 8) {
            this.modules[y][x] = ((data[i >>> 3] >>> (7 - (i & 7))) & 1) !== 0;
            i += 1;
          }
        }
      }
    }
  }

  applyMask(mask) {
    for (let y = 0; y < this.size; y += 1) {
      for (let x = 0; x < this.size; x += 1) {
        let invert;
        switch (mask) {
          case 0: invert = (x + y) % 2 === 0; break;
          case 1: invert = y % 2 === 0; break;
          case 2: invert = x % 3 === 0; break;
          case 3: invert = (x + y) % 3 === 0; break;
          case 4: invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break;
          case 5: invert = ((x * y) % 2) + ((x * y) % 3) === 0; break;
          case 6: invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break;
          case 7: invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; break;
          default: invert = false;
        }
        if (invert && !this.isFunction[y][x]) {
          this.modules[y][x] = !this.modules[y][x];
        }
      }
    }
  }

  getPenaltyScore() {
    let result = 0;
    const { size } = this;
    // Adjacent modules in rows/cols
    for (let y = 0; y < size; y += 1) {
      let runColor = false;
      let runX = 0;
      for (let x = 0; x < size; x += 1) {
        if (this.modules[y][x] === runColor) {
          runX += 1;
          if (runX === 5) result += 3;
          else if (runX > 5) result += 1;
        } else { runColor = this.modules[y][x]; runX = 1; }
      }
    }
    for (let x = 0; x < size; x += 1) {
      let runColor = false;
      let runY = 0;
      for (let y = 0; y < size; y += 1) {
        if (this.modules[y][x] === runColor) {
          runY += 1;
          if (runY === 5) result += 3;
          else if (runY > 5) result += 1;
        } else { runColor = this.modules[y][x]; runY = 1; }
      }
    }
    // 2x2 blocks
    for (let y = 0; y < size - 1; y += 1) {
      for (let x = 0; x < size - 1; x += 1) {
        const c = this.modules[y][x];
        if (c === this.modules[y][x + 1] && c === this.modules[y + 1][x] && c === this.modules[y + 1][x + 1]) {
          result += 3;
        }
      }
    }
    // Balance of dark/light
    let dark = 0;
    this.modules.forEach((row) => row.forEach((cell) => { if (cell) dark += 1; }));
    const total = size * size;
    const k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1;
    result += k * 10;
    return result;
  }
}

const encodeToCodewords = (text, version) => {
  const bytes = makeByteSegmentBits(text);
  const bb = [];
  appendBits(0x4, 4, bb); // byte mode indicator
  const charCountBits = version < 10 ? 8 : 16;
  appendBits(bytes.length, charCountBits, bb);
  bytes.forEach((b) => appendBits(b, 8, bb));

  const dataCapacityBits = getNumDataCodewords(version) * 8;
  // Terminator
  appendBits(0, Math.min(4, dataCapacityBits - bb.length), bb);
  // Byte-align
  appendBits(0, (8 - (bb.length % 8)) % 8, bb);
  // Pad bytes
  for (let padByte = 0xec; bb.length < dataCapacityBits; padByte ^= 0xec ^ 0x11) {
    appendBits(padByte, 8, bb);
  }

  const codewords = [];
  for (let i = 0; i < bb.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j += 1) byte = (byte << 1) | bb[i + j];
    codewords.push(byte);
  }
  return codewords;
};

/**
 * Encode `text` into a QR matrix using ECC level M. Automatically chooses the
 * smallest version (1..40) that fits, then selects the lowest-penalty mask.
 * @returns {boolean[][]} matrix where true = dark module
 */
export const generateQrMatrix = (text) => {
  const bytes = makeByteSegmentBits(text);

  let version = -1;
  for (let v = 1; v <= 40; v += 1) {
    const charCountBits = v < 10 ? 8 : 16;
    const usedBits = 4 + charCountBits + bytes.length * 8;
    const capacityBits = getNumDataCodewords(v) * 8;
    if (usedBits <= capacityBits) { version = v; break; }
  }
  if (version === -1) {
    throw new Error('Data too long for a QR code (max version 40)');
  }

  const dataCodewords = encodeToCodewords(text, version);

  // Try all 8 masks, keep the one with the lowest penalty.
  let bestQr = null;
  let bestPenalty = Infinity;
  for (let mask = 0; mask < 8; mask += 1) {
    const qr = new QrCode(version, ECC_MEDIUM, dataCodewords.slice(), mask);
    const penalty = qr.getPenaltyScore();
    if (penalty < bestPenalty) {
      bestPenalty = penalty;
      bestQr = qr;
    }
  }
  return bestQr.modules;
};

export default generateQrMatrix;
